import { useEffect, useState } from "react";
import { useWarRoomState } from "./state";
import { VeyraTab } from "./tabs/VeyraTab";
import { PeakTab } from "./tabs/PeakTab";
import { FleetTab } from "./tabs/FleetTab";
import { MetricsTab } from "./tabs/MetricsTab";
import { RoomView } from "./room/RoomView";
import { RightRail } from "./rail/RightRail";
import { CommandPalette } from "./components/CommandPalette";
import { StalenessBadge } from "./components/StalenessBadge";
import { SidePanel } from "./components/SidePanel";
import { FounderInboxStrip } from "./components/FounderInboxStrip";
import { avatarUrlFor, emojiFor, emojiDataUrl } from "./lanes";
import type { Gate, Lane } from "./types";

type TabKey = "room" | "veyra" | "peak" | "fleet" | "metrics";

export default function App() {
  const { snap, lastEventTs, wsState, refresh } = useWarRoomState();
  const [tab, setTab] = useState<TabKey>("veyra");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openGate, setOpenGate] = useState<Gate | null>(null);
  const [openLane, setOpenLane] = useState<Lane | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      const tgt = e.target as HTMLElement | null;
      const inField = tgt && (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA" || tgt.isContentEditable);
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (meta && e.key.toLowerCase() === "p") {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (!inField && !meta) {
        if (e.key === "1") { setTab("veyra"); return; }
        if (e.key === "2") { setTab("room"); return; }
        if (e.key === "3") { setTab("peak"); return; }
        if (e.key === "4") { setTab("fleet"); return; }
        if (e.key === "5") { setTab("metrics"); return; }
        if (e.key.toLowerCase() === "f") {
          alert("Hyperfocus arrives Week 4.");
          return;
        }
        if (e.key === "Escape") {
          setOpenGate(null);
          setOpenLane(null);
          setPaletteOpen(false);
          setSearchOpen(false);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const aggregatorConnected = Boolean(snap?.aggregator?.connected);

  return (
    <div className="h-full flex flex-col bg-ink text-white">
      <header className="flex items-center gap-6 border-b border-edge px-5 h-12 shrink-0">
        <div className="flex items-center gap-2 font-mono text-sm">
          <span className="text-glow">▰</span>
          <span className="font-semibold">RatCap War Room</span>
          <span className="text-mist text-xs">v0</span>
        </div>
        <nav className="flex gap-1 text-sm">
          <TabButton active={tab === "veyra"} onClick={() => setTab("veyra")}>
            VeyraPay <span className="text-mist text-xs ml-1">1</span>
          </TabButton>
          <TabButton active={tab === "room"} onClick={() => setTab("room")}>
            Room <span className="text-mist text-xs ml-1">2</span>
          </TabButton>
          <TabButton active={tab === "peak"} onClick={() => setTab("peak")}>
            Peak Peptides <span className="text-mist text-xs ml-1">3</span>
          </TabButton>
          <TabButton active={tab === "fleet"} onClick={() => setTab("fleet")}>
            Fleet <span className="text-mist text-xs ml-1">4</span>
          </TabButton>
          <TabButton active={tab === "metrics"} onClick={() => setTab("metrics")}>
            Metrics <span className="text-mist text-xs ml-1">5</span>
          </TabButton>
        </nav>
        <div className="ml-auto flex items-center gap-3 text-xs text-mist">
          {!aggregatorConnected && (
            <span className="text-warn">Aggregator not ready · showing soft-empty</span>
          )}
          <button
            onClick={() => setPaletteOpen(true)}
            className="border border-edge rounded px-2 py-0.5 hover:border-mist"
          >
            ⌘K  dispatch
          </button>
        </div>
      </header>

      <FounderInboxStrip inbox={snap?.inbox ?? []} todos={snap?.frankie_todos ?? []} />

      <main className="flex-1 min-h-0 flex">
        {tab === "room" ? (
          <section className="flex-1 min-w-0 min-h-0 overflow-hidden">
            <RoomView snap={snap} />
          </section>
        ) : (
          <>
            <section className="flex-1 min-w-0 overflow-auto scroll-thin p-5">
              {tab === "fleet" ? (
                <FleetTab />
              ) : tab === "metrics" ? (
                <MetricsTab snap={snap} />
              ) : !snap ? (
                <div className="text-mist text-sm">Loading…</div>
              ) : tab === "veyra" ? (
                <VeyraTab snap={snap} onOpenGate={setOpenGate} />
              ) : (
                <PeakTab snap={snap} />
              )}
            </section>
            <RightRail snap={snap} onOpenLane={setOpenLane} />
          </>
        )}
      </main>

      <StalenessBadge lastEventTs={lastEventTs} wsState={wsState} onRefresh={refresh} />

      {paletteOpen && (
        <CommandPalette
          onClose={() => setPaletteOpen(false)}
          lanes={snap?.fleet.lanes ?? []}
        />
      )}

      {searchOpen && (
        <SidePanel title="Search" onClose={() => setSearchOpen(false)} width={520}>
          <div className="text-mist text-sm">Search arrives Week 2.</div>
        </SidePanel>
      )}

      {openGate && (
        <SidePanel title={openGate.entity_name || openGate.slug} onClose={() => setOpenGate(null)} width={560}>
          <GateDetail gate={openGate} />
        </SidePanel>
      )}

      {openLane && (
        <SidePanel title={`${openLane.emoji} ${openLane.name}`} onClose={() => setOpenLane(null)} width={520}>
          <LaneDetail lane={openLane} />
        </SidePanel>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        "px-3 py-1 rounded text-sm transition " +
        (active ? "bg-edge text-white" : "text-mist hover:text-white")
      }
    >
      {children}
    </button>
  );
}

function GateDetail({ gate }: { gate: Gate }) {
  return (
    <div className="space-y-4 text-sm">
      <div>
        <div className="text-mist text-xs uppercase tracking-wide">Slug</div>
        <div className="font-mono">{gate.slug}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Fact label="Stage" value={gate.stage} />
        <Fact label="Owner" value={gate.owner_lane} />
        <Fact label="Last event" value={fmtTs(gate.last_event_ts)} />
        <Fact label="Account" value={gate.acct_id ?? "—"} />
      </div>
      <div>
        <div className="text-mist text-xs uppercase tracking-wide mb-1">Latest</div>
        <div>{gate.last_event_summary || "—"}</div>
      </div>
      <div className="border-t border-edge pt-3 text-mist text-xs">
        Actions arrive Week 2. Read-only for now.
      </div>
    </div>
  );
}

function LaneDetail({ lane }: { lane: Lane }) {
  const emoji = lane.emoji || emojiFor(lane.id);
  const src = lane.avatar_url && lane.avatar_url !== "" ? lane.avatar_url : avatarUrlFor(lane.id);
  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center gap-3">
        <span className="relative inline-block w-14 h-14">
          <img
            src={src}
            alt=""
            className="w-14 h-14 rounded-full bg-edge object-cover ring-1 ring-edge"
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = emojiDataUrl(emoji); }}
          />
          {emoji && (
            <span className="absolute -bottom-1 -right-1 text-base leading-none bg-ink rounded-full px-1 ring-1 ring-edge">
              {emoji}
            </span>
          )}
        </span>
        <div>
          <div className="text-base">{lane.name}</div>
          <div className="text-mist text-xs">{lane.signoff}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Fact label="Status" value={lane.status} />
        <Fact label="Machine" value={lane.machine} />
        <Fact label="Last action" value={fmtTs(lane.last_action_ts)} />
        <Fact label="Spend today" value={`$${(lane.cost_today_usd ?? 0).toFixed(2)}`} />
      </div>
      <div>
        <div className="text-mist text-xs uppercase tracking-wide mb-1">Last summary</div>
        <div>{lane.last_action_summary || "—"}</div>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-mist text-xs uppercase tracking-wide">{label}</div>
      <div className="font-mono text-xs">{value}</div>
    </div>
  );
}

function fmtTs(ts: string | null | undefined) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    const mins = Math.round((Date.now() - d.getTime()) / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch {
    return "—";
  }
}
